import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, ShoppingCart, User, Menu, X, LogOut, Clock, BookMarked, Phone, FileText, Headphones } from 'lucide-react';
interface StudentLayoutProps {
  children: React.ReactNode;
}
export function StudentLayout({
  children
}: StudentLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();
  const navigation = [{
    group: 'MY DASHBOARD',
    items: [{
      name: 'My Classes (currently enrolled)',
      href: '/student/my-classes',
      icon: BookMarked
    }, {
      name: 'Available Classes (enroll here)',
      href: '/student/available-classes',
      icon: BookOpen
    }, {
      name: 'Class History',
      href: '/student/class-history',
      icon: Clock
    }, {
      name: 'Manage Profile',
      href: '/student/profile',
      icon: User
    }]
  }, {
    group: 'MY SHOPPING CART',
    items: [{
      name: 'View Cart',
      href: '/student/cart',
      icon: ShoppingCart
    }, {
      name: 'Browse Packages/Classes',
      href: '/student/products',
      icon: BookOpen
    }, {
      name: 'Checkout',
      href: '/student/checkout',
      icon: ShoppingCart
    }, {
      name: 'Order History',
      href: '/student/orders',
      icon: Clock
    }]
  }];
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-231">
      {/* Top Bar */}
      <header className="bg-white border-b border-purple-100 h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30 shadow-sm" data-id="element-232">
        <div className="flex items-center gap-4" data-id="element-233">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-purple-50 rounded-md lg:hidden" data-id="element-234">
            <Menu className="h-6 w-6 text-purple-700" data-id="element-235" />
          </button>
          <h1 className="text-lg font-semibold text-purple-900 hidden sm:block" data-id="element-236">
            Student Portal
          </h1>
        </div>

        <div className="flex items-center gap-4" data-id="element-237"></div>
      </header>

      <div className="flex flex-1 relative" data-id="element-238">
        {/* Sidebar */}
        <aside className={`
            fixed inset-y-0 left-0 z-20 w-64 text-white transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-auto
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `} style={{
        backgroundColor: '#4A2D8F'
      }} data-id="element-239">
          <div className="h-full overflow-y-auto flex flex-col" data-id="element-240">
            {/* Logo */}
            <div className="p-4 border-b border-purple-700/50" data-id="element-241">
              <div className="flex items-center justify-between mb-2 lg:hidden" data-id="element-242">
                <button onClick={() => setIsSidebarOpen(false)} className="p-2 text-purple-300 hover:text-white" data-id="element-243">
                  <X className="h-6 w-6" data-id="element-244" />
                </button>
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/ghnAoYdYcsrnAteaBPC6VB/DSS-logo-notext.png" alt="Desert Sage Seminars" className="w-full max-w-[180px] h-auto" data-id="element-245" />
            </div>

            {/* Welcome section */}
            <div className="px-4 py-4 border-b border-purple-700/50" data-id="element-246">
              <p className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-1" data-id="element-247">
                Welcome
              </p>
              <p className="text-white font-bold text-lg" data-id="element-248">Dana Kichen</p>
            </div>

            {/* Nav */}
            <nav className="flex-1 p-4 space-y-6" data-id="element-249">
              {/* MY DASHBOARD group */}
              <div data-id="element-250">
                <h3 className="px-2 text-xs font-semibold text-purple-300 uppercase tracking-wider mb-2" data-id="element-251">
                  MY DASHBOARD
                </h3>
                <div className="space-y-0.5" data-id="element-252">
                  {navigation[0].items.map(item => {
                  const isActive = location.pathname === item.href;
                  return <Link key={item.name} to={item.href} className={`
                          group flex items-center px-2 py-2 text-xs font-medium rounded-md transition-colors
                          ${isActive ? 'bg-purple-700 text-white' : 'text-purple-200 hover:bg-purple-700/60 hover:text-white'}
                        `} data-id="element-253">
                        <item.icon className={`mr-2.5 h-4 w-4 flex-shrink-0 ${isActive ? 'text-yellow-400' : 'text-purple-400 group-hover:text-yellow-400'}`} data-id="element-254" />
                        {item.name}
                      </Link>;
                })}
                </div>
              </div>

              {/* Instructions button */}
              <div className="px-2" data-id="element-255">
                <Link to="/student/instructions" className={`
                    flex items-center justify-center px-3 py-1.5 rounded text-xs font-bold transition-colors
                    ${location.pathname === '/student/instructions' ? 'bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}
                  `} data-id="element-256">
                  <FileText className="mr-1.5 h-3.5 w-3.5" data-id="element-257" />
                  Instructions
                </Link>
              </div>

              {/* MY SHOPPING CART group */}
              <div data-id="element-258">
                <h3 className="px-2 text-xs font-semibold text-purple-300 uppercase tracking-wider mb-2" data-id="element-259">
                  MY SHOPPING CART
                </h3>
                <div className="space-y-0.5" data-id="element-260">
                  {navigation[1].items.map(item => {
                  const isActive = location.pathname === item.href;
                  return <Link key={item.name} to={item.href} className={`
                          group flex items-center px-2 py-2 text-xs font-medium rounded-md transition-colors
                          ${isActive ? 'bg-purple-700 text-white' : 'text-purple-200 hover:bg-purple-700/60 hover:text-white'}
                        `} data-id="element-261">
                        <item.icon className={`mr-2.5 h-4 w-4 flex-shrink-0 ${isActive ? 'text-yellow-400' : 'text-purple-400 group-hover:text-yellow-400'}`} data-id="element-262" />
                        {item.name}
                      </Link>;
                })}
                </div>
              </div>

              {/* Contact Info */}
              <div className="px-2 pt-1" data-id="element-263">
                <p className="text-xs font-semibold text-purple-300 uppercase tracking-wider mb-2" data-id="element-264">
                  Contact Information
                </p>
                <div className="flex items-start gap-2 text-xs text-purple-200" data-id="element-265">
                  <Phone className="h-3.5 w-3.5 mt-0.5 text-purple-400 flex-shrink-0" data-id="element-266" />
                  <div data-id="element-267">
                    <p className="text-purple-300" data-id="element-268">For Class Support:</p>
                    <p className="text-white font-medium" data-id="element-269">
                      Call or text: 602-377-6207
                    </p>
                  </div>
                </div>
              </div>

              {/* Listen instead of read */}
              <div className="px-2 pt-4" data-id="element-270">
                <Link to="/student/dashboard" className="group flex items-start gap-2 px-2 py-2 rounded-md text-purple-200 hover:bg-purple-700/60 hover:text-white transition-colors" data-id="element-271">
                  <Headphones className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-400 group-hover:text-yellow-400" data-id="element-272" />
                  <span className="text-xs leading-snug" data-id="element-273">
                    Prefer to listen instead of read?
                  </span>
                </Link>
              </div>

              {/* Log Off */}
              <div className="px-2 pt-2" data-id="element-274">
                <button className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-bold rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors" onClick={() => window.location.href = 'https://www.desertsageseminars.com'} data-id="element-275">
                  <LogOut className="h-4 w-4" data-id="element-276" />
                  Log Off
                </button>
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 w-full" data-id="element-277">
          <div className="max-w-7xl mx-auto" data-id="element-278">{children}</div>
        </main>

        {/* Mobile Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-10 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-279" />}
      </div>
    </div>;
}